import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import type { PaginatedDashboards } from '../types';
import Pagination from '../components/Pagination';

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="var(--tx3)">
    <path fillRule="evenodd" d="M6.5 1a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM0 6.5a6.5 6.5 0 1111.596 4.03l3.187 3.187a.75.75 0 11-1.06 1.06L10.536 11.59A6.5 6.5 0 010 6.5z" />
  </svg>
);

export default function DashboardList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [data, setData] = useState<PaginatedDashboards | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), per_page: '25' });
    if (search) params.set('search', search);
    try {
      setData(await api<PaginatedDashboards>(`/admin/dashboards?${params}`));
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const fmt = (dt: string | null) =>
    dt ? new Date(dt).toLocaleDateString('en', { month: 'short', day: 'numeric', year: '2-digit' }) : '—';

  return (
    <div className="page-content">
      <div style={{ marginBottom: 20 }}>
        <div className="overline">Platform</div>
        <h1 className="page-title">Dashboards</h1>
      </div>

      <div className="filter-bar">
        <div className="search-bar" style={{ flex: 1, maxWidth: 360 }}>
          <SearchIcon />
          <input
            placeholder="Search dashboards…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading && !data ? (
          <div className="loading-state"><div className="spinner" /></div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Owner</th>
                  <th>Articles (7d)</th>
                  <th>Auto-fetch</th>
                  <th>Lang</th>
                  <th>Last Active</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', color: 'var(--tx3)', padding: 32 }}>
                      No dashboards found
                    </td>
                  </tr>
                )}
                {data?.items.map(d => (
                  <tr key={d.id} className="clickable" onClick={() => navigate(`/dashboards/${d.id}`)}>
                    <td style={{ fontWeight: 500 }}>{d.name}</td>
                    <td style={{ color: 'var(--tx2)', fontSize: 12 }}>{d.owner_email}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{d.article_count_7d.toLocaleString()}</td>
                    <td>
                      {d.fetch_enabled
                        ? <span className="badge badge-ok">On</span>
                        : <span className="badge badge-neutral">Off</span>}
                    </td>
                    <td><span className="tag">{d.extraction_language.toUpperCase()}</span></td>
                    <td style={{ color: 'var(--tx3)' }}>{fmt(d.last_triggered_at)}</td>
                    <td style={{ color: 'var(--tx3)' }}>{fmt(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data && data.pages > 1 && (
              <div style={{ padding: '0 12px' }}>
                <Pagination page={page} pages={data.pages} total={data.total} perPage={25} onPage={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
